// src/store/slices/crowdstrikeSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCloudAccount } from '../../api/crowdstrikeApi';

const initialState = {
  // 三大標準的合規數據
  complianceSummary: {
    'ISO 27001:2022': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    'CIS 4.0.0 Azure Foundations': {
      passed_count: 0,
      total_count: 0,
      failed_count: 0,
      compliance_percentage: 0,
    },
    // total: {
    //   passed_count: 0,
    //   total_count: 0,
    //   failed_count: 0,
    //   compliance_percentage: 0,
    // },
  },

  // 標準詳細資料
  complianceDetail: {
    standardId: null,
    standardName: null,
    year: null,
    currentMonth: null,
    currentMonthKey: null,
    months: [],
    controls: [], // 控制項列表
    metadata: {},
  },

  // Assessment 的 Resource
  assessmentResources: {}, // key: assessmentId, value: { data: [], count: 0 }

  // 雲端查詢範圍（Azure subscription / GCP project / AWS account）
  cloudScopes: [], // [{ id, name, state }]
  selectedCloudScope: '',
  defaultCloudScopeId: null,
  scopesLoading: false,
  scopesLoaded: false,
  scopesError: null,

  // 選擇的詳細檢視標準
  selectedDetailStandard: 'iso_27001', // 'iso_27001' | 'cis' | 'mcsb'

  // 風險評估數據
  assessments: null,
  selectedStandard: 'iso_27001',

  // 統計數據（根據選中的標準）
  statistics: {
    criticalRisk: 0,
    highRisk: 0,
    mediumRisk: 0,
    lowRisk: 0,
    totalRisk: 0,
    passed: 0,
    failed: 0,
    notAssessed: 0,
    total: 0,
    compliancePercentage: 0,
  },

  metadata: {
    cached: false,
    cacheTimestamp: null,
    queryIdentifier: null,
    riskType: 'summary',
    standard: 'all',
    totalRecords: 0,
    count: 0,
    page: 1,
    pageSize: 100,
  },

  loading: false,
  summaryLoading: false,
  detailLoading: false,
  resourcesLoading: false,
  error: null,
};

// 取得此專案憑證可見的查詢範圍清單
export const fetchCloudScopes = createAsyncThunk(
  'crowdstrike/fetchCloudScopes',
  async (params = {}, { rejectWithValue }) => {
    const { platform = 'azure', month, scan_month } = params;
    const resolvedMonth = scan_month || month;

    try {
      const response = await getCloudAccount({
        cloud: platform,
        ...(resolvedMonth ? { scan_month: resolvedMonth } : {}),
      });
      return response || { scopes: [], default_scope_id: null };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || err.response?.data?.message || '取得查詢範圍清單失敗'
      );
    }
  }
);

const crowdstrikeSlice = createSlice({
  name: 'crowdstrike',
  initialState,
  reducers: {
    setSelectedStandard: (state, action) => {
      state.selectedStandard = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAssessments: (state) => {
      state.assessments = null;
      state.statistics = initialState.statistics;
      state.metadata = initialState.metadata;
    },

    // 設定選擇的詳細檢視標準
    setSelectedDetailStandard: (state, action) => {
      state.selectedDetailStandard = action.payload;
    },

    // 清除合規詳細資料
    clearComplianceDetail: (state) => {
      state.complianceDetail = initialState.complianceDetail;
      state.assessmentResources = {};
    },

    setSelectedCloudScope: (state, action) => {
      state.selectedCloudScope = action.payload || '';
      state.assessmentResources = {};
    },

    // 切換平台時清掉上一個雲的範圍狀態
    resetCloudScopes: (state) => {
      state.cloudScopes = [];
      state.selectedCloudScope = '';
      state.defaultCloudScopeId = null;
      state.scopesLoading = false;
      state.scopesLoaded = false;
      state.scopesError = null;
      state.assessmentResources = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCloudScopes.pending, (state) => {
        state.scopesLoading = true;
        state.scopesError = null;
      })
      .addCase(fetchCloudScopes.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const scopes = payload.scopes || payload.subscriptions || [];

        state.scopesLoading = false;
        state.scopesLoaded = true;
        state.scopesError = null;
        state.cloudScopes = scopes;
        state.defaultCloudScopeId = payload.default_scope_id || payload.default_subscription_id || null;

        const preferred =
          state.defaultCloudScopeId ||
          scopes[0]?.id ||
          '';

        const stillValid = scopes.some((scope) => scope.id === state.selectedCloudScope);
        if (!stillValid) {
          state.selectedCloudScope = preferred;
        }
      })
      .addCase(fetchCloudScopes.rejected, (state, action) => {
        state.scopesLoading = false;
        state.scopesLoaded = true;
        state.scopesError = action.payload || '取得查詢範圍清單失敗';
        state.cloudScopes = [];
        state.selectedCloudScope = '';
      });
  },
});

export const {
  setSelectedStandard,
  clearError,
  resetAssessments,
  setSelectedDetailStandard,
  clearComplianceDetail,
  setSelectedCloudScope,
  resetCloudScopes,
} = crowdstrikeSlice.actions;
export default crowdstrikeSlice.reducer;
